import { Link } from "react-router-dom";
import { MEDIA_SLOTS } from "../data/content";

const PROD_HOSTS = ["www.80metrics.com.au", "80metrics.com.au"];

// Owner staging aid ONLY: labelled placement frames appear exclusively on
// non-production hosts when ?media-preview=1 is present. Never an auth
// mechanism, never secrets — just generic layout labels.
export const isMediaPreview = () => {
  if (typeof window === "undefined") return false;
  if (PROD_HOSTS.includes(window.location.hostname)) return false;
  return new URLSearchParams(window.location.search).has("media-preview");
};

export const MediaSlot = ({ id, className = "" }) => {
  const slot = MEDIA_SLOTS.find((s) => s.id === id);
  if (!slot) return null;

  // Public mode with a genuine approved asset: real player, lazy, no autoplay.
  if (slot.status === "published" && slot.sourceUrl) {
    return (
      <figure className={className} data-testid={`media-slot-${id}`}>
        <video
          controls
          preload="none"
          poster={slot.posterUrl || undefined}
          aria-label={slot.title}
          className="w-full border border-charcoal/15 bg-charcoal"
        >
          <source src={slot.sourceUrl} />
          {slot.captionsUrl && (
            <track kind="captions" src={slot.captionsUrl} srcLang="en" label="English captions" default />
          )}
        </video>
        <figcaption className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-charcoal/60">
          <span className="font-semibold tracking-[0.18em] uppercase text-charcoal/70">{slot.title}</span>
          {slot.duration && <span>{slot.duration}</span>}
          {slot.transcriptUrl && (
            <a
              href={slot.transcriptUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`media-slot-${id}-transcript`}
              className="underline underline-offset-4 hover:text-golddark transition-colors duration-300"
            >
              Read the transcript
            </a>
          )}
        </figcaption>
      </figure>
    );
  }

  // Owner preview staging frame (preview hosts only, explicit toggle).
  if (isMediaPreview()) {
    return (
      <div
        className={`border border-dashed border-gold/70 bg-gold/5 aspect-video flex flex-col items-center justify-center gap-2 px-6 text-center ${className}`}
        data-testid={`media-slot-${id}`}
        data-preview-frame="true"
        role="note"
        aria-label={`Video placement — ${slot.title}`}
      >
        <span className="text-[11px] font-semibold tracking-[0.24em] uppercase text-golddark">Video placement</span>
        <span className="font-serif text-xl text-charcoal/85">{slot.title}</span>
        <span className="text-xs text-charcoal/50">Planned — this frame becomes the player once the approved video is added</span>
      </div>
    );
  }

  // Public mode without an asset: written explanation in the same position,
  // with a working anchor. No dead player, no fake controls.
  return (
    <div className={`border border-charcoal/15 bg-ivory p-7 lg:p-8 ${className}`} data-testid={`media-slot-${id}`}>
      <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-golddark">{slot.title}</p>
      <p className="mt-3 text-sm leading-relaxed text-charcoal/70">{slot.fallback}</p>
      <Link
        to={slot.fallbackLink.to}
        data-testid={`media-slot-${id}-link`}
        className="group mt-4 inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-charcoal hover:text-golddark transition-colors duration-300"
      >
        <span>{slot.fallbackLink.label}</span>
        <span className="inline-block transition-transform duration-300 group-hover:translate-x-1.5">→</span>
      </Link>
    </div>
  );
};
